/** @jsxImportSource theme-ui @jsxRuntime classic */
import React from 'react';
import {
  Flex,
  IntrinsicImage,
  Link,
  Section,
  Text,
  type TishmanThemeName,
} from '@tishman/components';

import { PrivateEventsImageCaption } from './PrivateEventsImageCaption';

type TPrivateEventsImageCallout = Queries.SanityImageCallout & {
  theme?: TishmanThemeName;
};

export const PrivateEventsImageCallout = ({
  title,
  description,
  image,
  link,
  theme = 'Rock Center Dark Gold',
}: TPrivateEventsImageCallout): JSX.Element | null => (
  <Section theme={theme}>
    <Flex
      sx={{
        justifyContent: 'space-between',
        gap: ['25px', '64px', '100px'],
        padding: ['64px 16px', '64px 32px'],

        flexDirection: ['column-reverse', 'column-reverse', 'row'],
        maxWidth: '1200px',
        margin: '0 auto',
        alignItems: 'center',
      }}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          gap: '30px',
          flex: 1,
          flexGrow: 1,
          alignItems: 'flex-start',
        }}
      >
        <Flex
          sx={{
            flexDirection: 'column',
            gap: '16px',
            alignSelf: 'stretch',
            width: '100%',
            flex: '0 0 auto',
            alignItems: 'flex-start',
          }}
        >
          <Text
            sx={{
              alignSelf: 'stretch',
              marginTop: '-1px',
              color: 'white',
            }}
            variant="h2"
          >
            {title}
          </Text>

          {description && (
            <Text
              mb={0}
              sx={{
                letterSpacing: 3,
                lineHeight: [1.4, 1.14],
                paddingRight: '4px',
                fontSize: ['19px', '21px'],
              }}
              variant="body"
            >
              {description}
            </Text>
          )}
        </Flex>

        <Link
          sx={{
            '&:hover': {
              bg: 'background',
              color: 'text',
            },
          }}
          to={link?.url || ''}
          variant="button"
        >
          {link?.caption}
        </Link>
      </Flex>

      {image?.asset && (
        <Flex
          sx={{
            flexDirection: 'column',
            alignItems: 'center',
            flex: '1 0 0',
            width: '100%',
            maxWidth: ['100%', '100%', '478px'],
            maxHeight: ['100%', '100%', '268px'],
            position: 'relative',
          }}
        >
          <IntrinsicImage
            alt={image.alt || image.caption || ''}
            image={image.asset.gatsbyImageData}
            sx={{
              width: '100%',
              height: '100%',
              aspectRatio: '16/9',
            }}
          />
          {image?.caption && <PrivateEventsImageCaption text={image.caption} />}
        </Flex>
      )}
    </Flex>
  </Section>
);
